import { FieldState, FormState } from '../../../../states/FormState';
import { SecureUser } from '../../../../types/user';
import { action, observable, makeObservable } from 'mobx';
import { UserApis } from '../../../../api/index';
import { ApiError } from '../../../../api/error';
import { checkState } from '../../../../utils/preconditions';

export type FieldSpec = {
  key: keyof UserInfo;
  label: string;
  placeholder: string;
  type?: 'text' | 'number' | 'password';
};

export type UserInfo = {
  name: string;
  furigana: string;
  email: string;
  age: number | string;
  phone_number: string;
  password: string;
  password_confirmation: string;
  job_company: string;
  job_department: string;
  job_title: string;
  facebook_url: string;
  via: string;
};

export enum FieldSetType {
  APPLICATION = 'application',
  EVENT = 'event',
  TRIAL = 'trial',
  SUCCINCT_TRIAL = 'succinct_trial',
}

export const DEFAULT_USER_SPECS: FieldSpec[] = [
  { key: 'name', label: 'お名前', placeholder: 'お名前（例：山田　花子）' },
  { key: 'furigana', label: 'フリガナ', placeholder: 'フリガナ（例：ヤマダ　ハナコ）' },
  { key: 'age', label: '年齢', type: 'number', placeholder: '年齢' },
  { key: 'email', label: 'メールアドレス', placeholder: 'メールアドレス' },
  { key: 'phone_number', label: '電話番号', placeholder: '電話番号（例: 080-xxxx-xxxx）' },
  { key: 'password', label: 'パスワード', placeholder: 'パスワード', type: 'password' },
  {
    key: 'password_confirmation',
    label: 'パスワード（再入力）',
    placeholder: 'パスワード（再入力）',
    type: 'password',
  },
  { key: 'job_company', label: '会社名', placeholder: '会社名' },
  { key: 'job_department', label: '部署名', placeholder: '部署名' },
  { key: 'job_title', label: '職種', placeholder: '職種' },
  { key: 'facebook_url', label: 'FacebookのURL', placeholder: 'FacebookのURL' },
];

export const EVENT_USER_SPECS: FieldSpec[] = [
  { key: 'name', label: 'お名前', placeholder: 'お名前（例：山田　花子）' },
  { key: 'email', label: 'メールアドレス', placeholder: 'メールアドレス' },
  { key: 'phone_number', label: '電話番号', placeholder: '電話番号（例: 080-xxxx-xxxx）' },
  { key: 'password', label: 'パスワード', placeholder: 'パスワード', type: 'password' },
  {
    key: 'password_confirmation',
    label: 'パスワード（再入力）',
    placeholder: 'パスワード（再入力）',
    type: 'password',
  },
  { key: 'job_company', label: '会社名', placeholder: '会社名' },
  { key: 'job_title', label: '職種', placeholder: '職種' },
];

export const TRIAL_USER_SPECS: FieldSpec[] = [
  { key: 'name', label: 'お名前', placeholder: 'お名前（例：山田　花子）' },
  { key: 'furigana', label: 'フリガナ', placeholder: 'フリガナ（例：ヤマダ　ハナコ）' },
  { key: 'age', label: '年齢', type: 'number', placeholder: '年齢' },
  { key: 'email', label: 'メールアドレス', placeholder: 'メールアドレス' },
  { key: 'phone_number', label: '電話番号', placeholder: '電話番号（例: 080-xxxx-xxxx）' },
  { key: 'password', label: 'パスワード', placeholder: 'パスワード', type: 'password' },
  {
    key: 'password_confirmation',
    label: 'パスワード（再入力）',
    placeholder: 'パスワード（再入力）',
    type: 'password',
  },
];

export const SUCCINCT_TRIAL_USER_SPECS: FieldSpec[] = [
  { key: 'name', label: 'お名前', placeholder: 'お名前（例：山田　花子）' },
  { key: 'email', label: 'メールアドレス', placeholder: 'メールアドレス' },
  { key: 'password', label: 'パスワード', placeholder: 'パスワード', type: 'password' },
  {
    key: 'password_confirmation',
    label: 'パスワード（再入力）',
    placeholder: 'パスワード（再入力）',
    type: 'password',
  },
];

function getBaseFieldSpecs(fieldSetType: FieldSetType): FieldSpec[] {
  switch (fieldSetType) {
    case FieldSetType.APPLICATION:
      return DEFAULT_USER_SPECS;
    case FieldSetType.EVENT:
      return EVENT_USER_SPECS;
    case FieldSetType.TRIAL:
      return TRIAL_USER_SPECS;
    case FieldSetType.SUCCINCT_TRIAL:
      return SUCCINCT_TRIAL_USER_SPECS;
    default:
      throw new Error('Unknown field set type : ' + fieldSetType);
  }
}

function getFieldSpecs(fieldSetType: FieldSetType, user?: SecureUser) {
  const baseSpecs = getBaseFieldSpecs(fieldSetType);
  if (!user) {
    return baseSpecs;
  }
  return baseSpecs.filter((spec) => {
    // If password is not set to the user
    // We need to show password form
    if (!user.has_password && spec.key.includes('password')) {
      return true;
    }
    return spec.key in user && !user[spec.key];
  });
}

export enum UserType {
  NEW_USER = 0,
  EXISTING_USER = 1,
}

export class UserFormState extends FormState<UserInfo> {
  fieldSetType: FieldSetType;
  fieldSpecs: FieldSpec[] = [];
  userType: UserType;
  constructor(requiredFields: readonly (keyof UserInfo)[] = []) {
    super({
      name: new FieldState('', requiredFields.includes('name')),
      furigana: new FieldState('', requiredFields.includes('furigana')),
      age: new FieldState(null, requiredFields.includes('age')),
      email: new FieldState('', requiredFields.includes('email')),
      phone_number: new FieldState('', requiredFields.includes('phone_number')),
      password: new FieldState('', requiredFields.includes('password')),
      password_confirmation: new FieldState('', requiredFields.includes('password_confirmation')),
      job_company: new FieldState('', requiredFields.includes('job_company')),
      job_department: new FieldState('', requiredFields.includes('job_department')),
      job_title: new FieldState('', requiredFields.includes('job_title')),
      facebook_url: new FieldState('', requiredFields.includes('facebook_url')),
      via: new FieldState('', requiredFields.includes('via')),
    });

    makeObservable(this, {
      fieldSetType: observable,
      fieldSpecs: observable,
      userType: observable,
      init: action,
      validate: action,
      submit: action,
    });
  }

  /**
   * Prefills the form fields
   * @param {SecureUser} user : specifies the set of fields to ask
   * @param {FieldSetType} fieldSet : specifies the set of fields to ask
   */
  init(user: SecureUser, fieldSet: FieldSetType) {
    if (!user) {
      this.userType = UserType.NEW_USER;
    } else {
      this.setValue('name', user.name || '');
      this.setValue('age', user.age);
      this.setValue('furigana', user.furigana || '');
      this.setValue('email', user.email || '');
      this.setValue('phone_number', user.phone_number || '');
      this.setValue('job_company', user.job_company || '');
      this.setValue('job_department', user.job_department || '');
      this.setValue('job_title', user.job_title || '');
      this.setValue('facebook_url', user.facebook_url || '');
      this.userType = UserType.EXISTING_USER;
    }
    this.fieldSetType = fieldSet;
    this.fieldSpecs = getFieldSpecs(this.fieldSetType, user);
  }

  /**
   * Returns true if there are fields we need to ask from this user
   */
  needUserInput(): boolean {
    return this.fieldSpecs.length > 0;
  }

  async validate() {
    try {
      this.clearErrors();
      await UserApis.validateForm(this.asJson(), this.fieldSetType);
    } catch (e) {
      if (e instanceof ApiError && e.isValidationError()) {
        this.setErrors(e.body);
      } else {
        throw e;
      }
    }
  }

  asJson() {
    const json = Object();
    this.fieldSpecs.forEach((spec) => {
      json[spec.key] = this.getValue(spec.key);
    });
    return json;
  }

  async submit(currentUser: SecureUser) {
    checkState(!this.hasErrors(), 'You cannot submit the form with errors');
    if (currentUser) {
      return await UserApis.updateUser(this.asJson());
    } else {
      return await UserApis.createUser(this.asJson());
    }
  }
}
